import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Star, ArrowLeft, MessageSquare, BookOpen } from "lucide-react";

const ATTRS = [
  { key: "punctuality", label: "Punctuality" },
  { key: "fluency", label: "Fluency of language" },
  { key: "content", label: "Content quality" },
  { key: "availability", label: "Availability" },
];

export default function MentorReviews() {
  const { id } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsMeta, setReviewsMeta] = useState({ total: 0, shown: 0, truncated: false, limit: null });
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all"); // all | course | session
  const [minStars, setMinStars] = useState(0); // 0 = all, or 1..5 => min

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        api.get(`/teachers/${id}`),
        api.get(`/teachers/${id}/reviews`),
      ]);
      setTeacher(t.data);
      // Iter34: endpoint now returns {reviews, total, shown, truncated, limit} for Free-plan gating.
      // Fall back to raw array for older clients / tests.
      const payload = r.data;
      if (Array.isArray(payload)) {
        setReviews(payload);
        setReviewsMeta({ total: payload.length, shown: payload.length, truncated: false, limit: null });
      } else {
        setReviews(payload.reviews || []);
        setReviewsMeta({
          total: payload.total ?? 0,
          shown: payload.shown ?? 0,
          truncated: !!payload.truncated,
          limit: payload.limit ?? null,
        });
      }
    } catch {
      toast.error("Could not load mentor reviews");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return reviews.filter((r) => {
      if (kindFilter !== "all" && r.kind !== kindFilter) return false;
      if (minStars > 0 && Number(r.rating || 0) < minStars) return false;
      return true;
    });
  }, [reviews, kindFilter, minStars]);

  const distribution = useMemo(() => {
    // count of reviews per star (5..1)
    const buckets = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((r) => {
      const s = Math.round(Number(r.rating || 0));
      if (s >= 1 && s <= 5) buckets[s] += 1;
    });
    return buckets;
  }, [reviews]);

  const total = reviews.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FFF7ED" }} data-testid="reviews-loading">
        <p className="font-bold" style={{ color: "#13A699" }}>Loading mentor reviews…</p>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FFF7ED" }}>
        <p style={{ color: "#13A699" }}>Mentor not found.</p>
      </div>
    );
  }

  const photoUrl = teacher.photo_path ? `${API}/files/${teacher.photo_path}` : "https://cdn.jsdelivr.net/gh/twitter/twemoji@v14.0.2/assets/svg/1f9d1-200d-1f4bb.svg";

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: "#FFF7ED" }} data-testid="mentor-reviews-page">
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Back link */}
        <Link
          to={`/teacher/${id}`}
          className="inline-flex items-center gap-1.5 text-sm font-bold"
          style={{ color: "#13A699" }}
          data-testid="back-to-mentor-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to mentor
        </Link>

        {/* Header card */}
        <section className="bg-white rounded-3xl p-5 border border-black/5 flex flex-col md:flex-row gap-5 md:items-center">
          <img
            src={photoUrl}
            alt={teacher.name}
            loading="eager"
            decoding="async"
            className="h-24 w-24 rounded-full object-cover border border-black/10"
          />
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl font-bold" style={{ color: "#13A699" }} data-testid="reviews-mentor-name">
              {teacher.name}
            </h1>
            <p className="text-sm mt-1" style={{ color: "#13A69999" }}>
              {(teacher.subjects || []).slice(0, 4).join(" · ")}
            </p>
            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
              <div className="flex items-baseline gap-1.5">
                <span className="text-4xl font-black leading-none" style={{ color: "#13A699" }} data-testid="reviews-avg-rating">
                  {teacher.rating_avg ? Number(teacher.rating_avg).toFixed(1) : "—"}
                </span>
                <Star className="h-5 w-5 fill-current" style={{ color: "#FFD708" }} />
              </div>
              <span className="text-sm font-semibold" style={{ color: "#13A69999" }} data-testid="reviews-total-count">
                {total} review{total === 1 ? "" : "s"}
              </span>
            </div>
          </div>
        </section>

        {/* Attribute breakdown + Distribution bars */}
        <section className="grid md:grid-cols-2 gap-4">
          {/* Attribute breakdown */}
          <div className="bg-white rounded-3xl p-5 border border-black/5" data-testid="reviews-attribute-breakdown">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider mb-3" style={{ color: "#13A699" }}>
              Explorer feedback by attribute
            </h2>
            <div className="space-y-2.5">
              {ATTRS.map(({ key, label }) => {
                const b = (teacher.rating_breakdown || {})[key] || {};
                const avg = b.avg;
                const count = b.count || 0;
                const pct = avg ? (Number(avg) / 5) * 100 : 0;
                return (
                  <div key={key} data-testid={`attr-row-${key}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold" style={{ color: "#13A699" }}>{label}</span>
                      <span className="text-xs font-bold" style={{ color: "#13A699" }}>
                        {avg != null ? `${Number(avg).toFixed(1)} ★` : "—"}
                        <span className="opacity-60"> ({count})</span>
                      </span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full" style={{ background: "#13A69922" }}>
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${pct}%`, background: "#13A699" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rating distribution */}
          <div className="bg-white rounded-3xl p-5 border border-black/5" data-testid="reviews-distribution">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider mb-3" style={{ color: "#13A699" }}>
              Rating distribution
            </h2>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((s) => {
                const count = distribution[s] || 0;
                const pct = total > 0 ? (count / total) * 100 : 0;
                return (
                  <button
                    key={s}
                    onClick={() => setMinStars(minStars === s ? 0 : s)}
                    className="w-full flex items-center gap-3 text-xs font-bold rounded-lg px-2 py-1.5 transition-colors"
                    style={{
                      background: minStars === s ? "#13A69911" : "transparent",
                      color: "#13A699",
                    }}
                    data-testid={`dist-row-${s}`}
                  >
                    <span className="w-8 text-left">{s} ★</span>
                    <div className="flex-1 h-2 rounded-full" style={{ background: "#13A69922" }}>
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${pct}%`, background: "#FFD708" }}
                      />
                    </div>
                    <span className="w-10 text-right">{count}</span>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[11px]" style={{ color: "#13A69988" }}>
              Tap a row to filter reviews with ≥ that many stars.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-white rounded-2xl p-3 border border-black/5 flex items-center gap-2 flex-wrap" data-testid="reviews-filters">
          <span className="text-xs font-bold uppercase" style={{ color: "#13A699AA" }}>Filter:</span>
          {[
            { k: "all", label: "All", icon: null },
            { k: "course", label: "Courses", icon: BookOpen },
            { k: "session", label: "Sessions", icon: MessageSquare },
          ].map((f) => {
            const active = kindFilter === f.k;
            const Icon = f.icon;
            return (
              <button
                key={f.k}
                onClick={() => setKindFilter(f.k)}
                className="text-xs font-bold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5"
                style={{
                  background: active ? "#13A699" : "#FFF7ED",
                  color: active ? "#FFF7ED" : "#13A699",
                  borderColor: active ? "#13A699" : "#13A69922",
                }}
                data-testid={`reviews-filter-${f.k}`}
              >
                {Icon && <Icon className="h-3.5 w-3.5" />} {f.label}
              </button>
            );
          })}
          {minStars > 0 && (
            <button
              onClick={() => setMinStars(0)}
              className="text-xs font-bold px-3 py-1.5 rounded-full border ml-auto"
              style={{ borderColor: "#F97316", color: "#F97316" }}
              data-testid="reviews-clear-stars"
            >
              Clear ≥{minStars}★ filter ✕
            </button>
          )}
        </section>

        {/* Reviews list */}
        <section className="space-y-3" data-testid="reviews-list">
          {reviewsMeta.truncated && (
            <div
              className="rounded-2xl p-4 border-2 flex flex-col sm:flex-row sm:items-center gap-3"
              style={{ background: "#FFF7ED", borderColor: "#FFD708" }}
              data-testid="reviews-upgrade-cta"
            >
              <div className="flex-1">
                <p className="text-sm font-bold" style={{ color: "#13A699" }}>
                  Showing {reviewsMeta.shown} of {reviewsMeta.total} reviews
                </p>
                <p className="text-xs mt-1" style={{ color: "#13A69999" }}>
                  Upgrade to <b>Explorer Plus</b> (₹299/mo) to unlock all mentor reviews plus priority support, early access to new mentors, family calendar, and class reminders.
                </p>
              </div>
              <Link
                to="/pricing"
                className="text-xs font-bold px-4 py-2 rounded-full"
                style={{ background: "#13A699", color: "#FFF7ED" }}
                data-testid="reviews-upgrade-btn"
              >
                See Explorer Plus →
              </Link>
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-3xl p-8 text-center border border-black/5" data-testid="reviews-empty">
              <p className="text-sm" style={{ color: "#13A69988" }}>
                {total === 0
                  ? "No reviews yet — be the first to enroll and rate this mentor."
                  : "No reviews match the current filters."}
              </p>
            </div>
          ) : (
            filtered.map((r, idx) => (
              <article
                key={idx}
                className="bg-white rounded-2xl p-4 border border-black/5"
                data-testid={`review-card-${idx}`}
              >
                <header className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-2">
                      <span className="font-bold text-sm" style={{ color: "#13A699" }}>{r.parent_name}</span>
                      <span
                        className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                        style={{
                          background: r.kind === "course" ? "#13A69911" : "#F9731611",
                          color: r.kind === "course" ? "#13A699" : "#F97316",
                        }}
                      >
                        {r.kind === "course" ? "Course" : "Session"}
                      </span>
                    </div>
                    {r.course_title && (
                      <p className="text-xs mt-0.5" style={{ color: "#13A69999" }}>
                        {r.course_title}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="inline-flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className="h-4 w-4"
                          style={{
                            color: n <= Number(r.rating || 0) ? "#FFD708" : "#13A69922",
                            fill: n <= Number(r.rating || 0) ? "#FFD708" : "transparent",
                          }}
                        />
                      ))}
                    </div>
                    <p className="text-[10px] mt-1" style={{ color: "#13A69988" }}>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : ""}
                    </p>
                  </div>
                </header>
                {r.review && (
                  <p className="mt-2 text-sm whitespace-pre-wrap" style={{ color: "#13A699" }}>
                    {r.review}
                  </p>
                )}
              </article>
            ))
          )}
        </section>
      </div>
    </div>
  );
}
